import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  X, Mail, Phone, Briefcase, Users,
  Clock, Calendar, FileText,
  Loader, DollarSign,
  Video, Link2, Plus, ArrowRight, Percent,
  UserPlus, ChevronRight, Edit2, Trash2, User as UserIcon,
  Mic, Square, Paperclip, Send,
  Bold, Italic, Underline, List, ListOrdered,
} from "lucide-react";
import {
  logInteraction,
  updateInteraction,
  getContactPersons,
  addContactPerson,
  updateContactPerson,
  deleteContactPerson,
  setPrimaryContactPerson,
  sendEmailInteraction,
  logInteractionWithRecording,
} from "../api/services";
import { useToast } from "../Auth-Context/ToastContext";
import { useAuth } from "../Auth-Context/AuthContext";
import AddContactPersonModal from "./AddContactPersonModal";
import AllContactPersonsModal from "./AllContactPersonsModal";
import EmailRecipientsField, {
  joinEmails,
  validateRecipientLists,
} from "./EmailRecipientsField";
import { RecordingPlayer, AttachmentLink, InteractionNotes } from "./InteractionMedia";

const STAGES = [
  { id: "prospecting",              name: "Prospecting",            color: "#8B5CF6", status: "PROSPECTING" },
  { id: "lead_qualification",       name: "Lead Qualification",     color: "#EC4899", status: "LEAD_QUALIFICATION" },
  { id: "discovery_pain_points",    name: "Discovery/Pain Points",  color: "#F59E0B", status: "DISCOVERY_PAIN_POINTS" },
  { id: "demo_pitch",               name: "Demo/Pitch",             color: "#10B981", status: "DEMO_PITCH" },
  { id: "proposal",                 name: "Proposal",               color: "#3B82F6", status: "PROPOSAL" },
  { id: "negotiation",              name: "Negotiation",            color: "#6366F1", status: "NEGOTIATION" },
  { id: "closed_won",               name: "Closed Won",             color: "#059669", status: "CLOSED_WON" },
  { id: "closed_lost",              name: "Closed Lost",            color: "#EF4444", status: "CLOSED_LOST" },
  { id: "customer",                 name: "Customer",               color: "#009DB9", status: "CUSTOMER" },
];

const LOG_TYPES = ["CALL", "EMAIL", "MEETING", "NOTE"];

const BCC_ALLOWED_ROLES = ["SALES_REP", "MANAGER", "ADMIN", "SUPER_ADMIN"];

const LOG_TYPE_COLORS = {
  CALL: { bg: "#dcfce7", color: "#16a34a", icon: Phone },
  EMAIL: { bg: "#dbeafe", color: "#1d4ed8", icon: Mail },
  MEETING: { bg: "#ede9fe", color: "#7c3aed", icon: Calendar },
  NOTE: { bg: "#fef3c7", color: "#d97706", icon: FileText },
};

const NOTE_CATEGORY_RESEARCH = "RESEARCH MAPPING";
const NOTE_CATEGORY_FOLLOWUP = "FOLLOW-UP";
const NOTE_CATEGORY_OFFICE_VISIT = "OFFICE VISIT";

const NOTE_CATEGORY_LABELS = {
  RESEARCH: NOTE_CATEGORY_RESEARCH,
  FOLLOWUP: NOTE_CATEGORY_FOLLOWUP,
  OFFICE_VISIT: NOTE_CATEGORY_OFFICE_VISIT,
};

const NOTE_CATEGORY_OPTIONS = [
  { key: "RESEARCH",     label: "Research Mapping" },
  { key: "FOLLOWUP",     label: "Follow-up" },
  { key: "OFFICE_VISIT", label: "Office Visit" },
];

const NOTE_CATEGORY_QUALIFICATION = "QUALIFICATION RESULT";

const MAX_CONTACT_PERSONS = 10;

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function getQualificationBadge(subject) {
  if (!subject || !subject.startsWith(NOTE_CATEGORY_QUALIFICATION)) return null;
  const prefix = `${NOTE_CATEGORY_QUALIFICATION} - `;
  const detail = subject.startsWith(prefix) ? subject.slice(prefix.length).trim() : "";
  const isQualified = detail.toLowerCase() === "qualified";
  return {
    label: detail || (isQualified ? "Qualified" : "Not Qualified"),
    isQualified,
  };
}

function stripHtmlToText(html) {
  if (!html) return "";
  const text = typeof html === "string" ? html : String(html || "");
  const doc = new DOMParser().parseFromString(text, "text/html");
  return (doc.body?.textContent || "").replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();
}

function getLoggedByDisplay(interaction) {
  return interaction.userName || interaction.userId || "";
}

function isEdited(log) {
  if (!log?.createdAt || !log?.updatedAt) return false;
  const created = new Date(log.createdAt).getTime();
  const updated = new Date(log.updatedAt).getTime();
  if (isNaN(created) || isNaN(updated)) return false;
  return updated - created > 1000;
}

function NoteCategoryCheckboxes({ value, onChange }) {
  return (
    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
      {NOTE_CATEGORY_OPTIONS.map((opt) => {
        const checked = value === opt.key;
        return (
          <label
            key={opt.key}
            className="inline-flex items-center gap-2 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onChange(checked ? null : opt.key)}
              className="w-4 h-4 rounded border-neutral-300 accent-[#009DB9] cursor-pointer"
            />
            <span
              className="text-[12px] font-semibold"
              style={{ color: checked ? "#009DB9" : "#525252" }}
            >
              {opt.label}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function AudioRecorder({ onRecordingComplete, onClear, onError, disabled }) {
  const [status, setStatus] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      stopStream();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      onError?.("Audio recording isn't supported in this browser.");
      return;
    }

    setStatus("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const supportedType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find(
        (t) => MediaRecorder.isTypeSupported?.(t)
      );
      const recorder = supportedType ? new MediaRecorder(stream, { mimeType: supportedType }) : new MediaRecorder(stream);

      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = () => {
        onError?.("Recording failed unexpectedly. Please try again.");
        clearInterval(timerRef.current);
        stopStream();
        setStatus("idle");
      };
      recorder.onstop = () => {
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: supportedType || "audio/webm" });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setStatus("stopped");
        stopStream();
        onRecordingComplete?.(blob, url, elapsedRef.current);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();

      elapsedRef.current = 0;
      setElapsed(0);
      setStatus("recording");
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
      }, 1000);
    } catch (err) {
      console.error("Microphone access failed:", err);
      setStatus("idle");
      if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") {
        onError?.("Microphone access was denied. Please allow microphone access to record the call.");
      } else if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
        onError?.("No microphone was found on this device.");
      } else {
        onError?.("Couldn't start recording. Please try again.");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && status === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const clearRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setStatus("idle");
    setElapsed(0);
    onClear?.();
  };

  const formatElapsed = (seconds = 0) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Audio Recording</label>

      {status === "idle" && (
        <button
          type="button"
          onClick={startRecording}
          disabled={disabled}
          className="flex items-center justify-center gap-2 h-9 rounded-lg border border-neutral-200 bg-white text-[12px] font-semibold text-neutral-700 hover:border-[#009DB9] hover:text-[#009DB9] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Mic size={14} /> Start Recording
        </button>
      )}

      {status === "requesting" && (
        <div className="flex items-center justify-center gap-2 h-9 text-[12px] text-neutral-500 border border-neutral-200 rounded-lg">
          <Loader size={14} className="animate-spin" /> Requesting microphone access…
        </div>
      )}

      {status === "recording" && (
        <div className="flex items-center justify-between gap-2 px-3 border border-red-200 rounded-lg h-9 bg-red-50">
          <div className="flex items-center gap-2">
            <span className="relative flex w-2.5 h-2.5">
              <span className="absolute inline-flex w-full h-full bg-red-400 rounded-full opacity-75 animate-ping" />
              <span className="relative inline-flex w-2.5 h-2.5 bg-red-500 rounded-full" />
            </span>
            <span className="text-[12px] font-semibold text-red-600">
              Recording… {formatElapsed(elapsed)}
            </span>
          </div>
          <button
            type="button"
            onClick={stopRecording}
            className="flex items-center gap-1.5 px-3 h-7 rounded-lg text-[11px] font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            <Square size={10} fill="white" /> Stop
          </button>
        </div>
      )}

      {status === "stopped" && audioUrl && (
        <div className="flex flex-col gap-2 p-2.5 border border-neutral-200 rounded-lg bg-neutral-50">
          <audio controls src={audioUrl} className="w-full h-9" />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-neutral-400">Duration: {formatElapsed(elapsed)}</span>
            <button
              type="button"
              onClick={clearRecording}
              disabled={disabled}
              className="text-[11px] font-semibold text-red-500 hover:underline disabled:opacity-40"
            >
              Remove &amp; re-record
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ open, onClose, children, width = "max-w-xl" }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${width} flex flex-col max-h-[90vh]`}>
        {children}
      </div>
    </div>
  );
}

function EmailBodyEditorModal({ initialHtml, onCancel, onSave }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialHtml || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
  };

  const handleSave = () => {
    onSave(editorRef.current?.innerHTML || "");
  };

  return (
    <Modal open onClose={onCancel} width="max-w-xl">
      <div className="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-neutral-100">
        <h3 className="text-[15px] font-bold text-neutral-900">Email Body</h3>
        <button onClick={onCancel} className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100">
          <X size={15} />
        </button>
      </div>

      <div className="flex items-center flex-shrink-0 gap-1 px-4 py-2 border-b border-neutral-100">
        <button type="button" onClick={() => exec("bold")} title="Bold"
          className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
          <Bold size={14} />
        </button>
        <button type="button" onClick={() => exec("italic")} title="Italic"
          className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
          <Italic size={14} />
        </button>
        <button type="button" onClick={() => exec("underline")} title="Underline"
          className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
          <Underline size={14} />
        </button>
        <span className="w-px h-5 mx-1 bg-neutral-200" />
        <button type="button" onClick={() => exec("insertUnorderedList")} title="Bullet list"
          className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
          <List size={14} />
        </button>
        <button type="button" onClick={() => exec("insertOrderedList")} title="Numbered list"
          className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-600 hover:bg-neutral-100">
          <ListOrdered size={14} />
        </button>
      </div>

      <div className="flex-1 px-6 py-4 overflow-y-auto">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="min-h-[220px] text-[13px] text-neutral-800 outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        />
      </div>

      <div className="flex items-center justify-end flex-shrink-0 gap-3 px-6 py-4 border-t border-neutral-100">
        <button onClick={onCancel} className="px-4 py-2 text-[13px] font-medium rounded-xl text-neutral-600 hover:bg-neutral-100">
          Cancel
        </button>
        <button onClick={handleSave} className="px-5 py-2 text-[13px] font-semibold text-white rounded-xl" style={{ background: "#009DB9" }}>
          Done
        </button>
      </div>
    </Modal>
  );
}

function DealCard({ deal, onEdit }) {
  const stage = STAGES.find((s) => s.status === deal.stage);
  const isWon = deal.stage === "CLOSED_WON";
  const isLost = deal.stage === "CLOSED_LOST";

  return (
    <div className="p-4 transition-shadow bg-white border border-neutral-100 rounded-xl hover:shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[13px] font-semibold text-neutral-800 leading-tight">{deal.title}</span>
        <button
          onClick={() => onEdit(deal)}
          className="flex-shrink-0 text-[10px] font-semibold px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors"
          style={{ background: "rgba(0,157,185,0.1)", color: "#009DB9" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.2)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.1)")}
        >
          Edit <ArrowRight size={9} strokeWidth={2.5} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {stage && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5"
            style={{ background: `${stage.color}18`, color: stage.color }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: stage.color }} />
            {stage.name}
          </span>
        )}
        {deal.probability !== undefined && (
          <span className="inline-flex items-center gap-1 text-[10px] text-neutral-400">
            <Percent size={9} />
            {deal.probability}%
          </span>
        )}
      </div>

      <div className="flex items-center justify-between">
        {deal.value ? (
          <div className="flex items-center gap-1">
            <DollarSign size={11} className={isWon ? "text-emerald-500" : "text-neutral-400"} />
            <span className={`text-[12px] font-bold ${isWon ? "text-emerald-600" : "text-neutral-700"}`}>
              {deal.currency || ""} {Number(deal.value).toLocaleString()}
            </span>
          </div>
        ) : (
          <span className="text-[11px] text-neutral-300">No value set</span>
        )}

        {deal.expectedCloseDate && (
          <div className="flex items-center gap-1 text-[10px] text-neutral-400">
            <Calendar size={9} />
            {new Date(deal.expectedCloseDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
        )}
      </div>

      {isLost && deal.lostReason && (
        <div className="pt-2 mt-2 border-t border-red-100">
          <p className="text-[10px] text-red-500 line-clamp-2">{deal.lostReason}</p>
        </div>
      )}
    </div>
  );
}

function getPersonFullName(person) {
  if (!person) return "";
  return person.fullName || `${person.firstName || ""} ${person.lastName || ""}`.trim();
}

function getPersonInitials(person) {
  if (!person) return "?";
  if (person.firstName && person.lastName) {
    return (person.firstName.charAt(0) + person.lastName.charAt(0)).toUpperCase();
  }
  if (person.fullName) {
    return person.fullName.substring(0, 2).toUpperCase();
  }
  return "?";
}

function getPersonDisplayInfo(person) {
  if (!person) return { name: "", initials: "?", contactInfo: "" };
  const name = getPersonFullName(person);
  const initials = getPersonInitials(person);
  const contactInfo = person.email || person.phone || person.mobile || "No contact info";
  return { name, initials, contactInfo };
}

export default function ContactModal({
  contact,
  open,
  onClose,
  onOpenDealForm,
  interactions,
  loadingInteractions,
  onRefreshInteractions,
}) {
  const toast = useToast();
  const { user } = useAuth();
  const canUseBcc = BCC_ALLOWED_ROLES.includes(user?.role);

  const [tab, setTab] = useState("info");
  const [logType, setLogType] = useState("CALL");

  const [logSubject, setLogSubject] = useState("");

  const [noteCategory, setNoteCategory] = useState(null);
  const [noteDetail, setNoteDetail] = useState("");

  const [logNotes, setLogNotes] = useState("");
  const [savingLog, setSavingLog] = useState(false);

  const [primaryPhone, setPrimaryPhone] = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");

  const [callMode, setCallMode] = useState("notes");
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioDurationSeconds, setAudioDurationSeconds] = useState(0);

  const [primaryEmail, setPrimaryEmail] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);

  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");
  const [emailBodyHtml, setEmailBodyHtml] = useState("");
  const [showEmailBodyModal, setShowEmailBodyModal] = useState(false);

  const [meetingLink, setMeetingLink] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingDuration, setMeetingDuration] = useState("");

  const [formKey, setFormKey] = useState(0);

  const [editingLog, setEditingLog] = useState(null);

  const notesTextareaRef = useRef(null);
  const resizeNotesTextarea = useCallback(() => {
    const el = notesTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(() => {
    resizeNotesTextarea();
  }, [logNotes, resizeNotesTextarea, tab]);

  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);

  const [persons, setPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showAllPersons, setShowAllPersons] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);

  const fetchDeals = async () => {
    if (!contact?.id) return;
    setLoadingDeals(true);
    try {
      const { listDeals } = await import("../api/services");
      const res = await listDeals({ size: 50 });
      const contactDeals = (res.content || []).filter(
        (d) => d.contactId === contact.id
      );
      setDeals(contactDeals);
    } catch (err) {
      console.error("Failed to fetch deals:", err);
      setDeals([]);
    } finally {
      setLoadingDeals(false);
    }
  };

  const fetchPersons = useCallback(async () => {
    if (!contact?.id) return;
    setLoadingPersons(true);
    try {
      const data = await getContactPersons({ contactId: contact.id });
      setPersons(data || []);
    } catch (err) {
      console.error("Failed to fetch contact persons:", err);
      setPersons([]);
    } finally {
      setLoadingPersons(false);
    }
  }, [contact?.id]);

  const handleTabChange = (id) => {
    setTab(id);
    if (id === "deals") fetchDeals();
    if (id === "people") fetchPersons();
  };

  useEffect(() => {
    if (!open) {
      setPersons([]);
      setEditingPerson(null);
    }
  }, [open, contact?.id]);

  useEffect(() => {
    if (open && contact?.id) {
      fetchPersons();
    }
  }, [open, contact?.id, fetchPersons]);

  const handleSavePerson = async (personData) => {
    if (editingPerson) {
      await updateContactPerson({
        contactId: contact.id,
        personId: editingPerson.id,
        ...personData,
      });
    } else {
      await addContactPerson({
        contactId: contact.id,
        ...personData,
      });
    }
    await fetchPersons();
  };

  const handleEditPerson = (person) => {
    setEditingPerson(person);
    setShowAddPerson(true);
  };

  const handleDeletePerson = async (personId) => {
    try {
      await deleteContactPerson({ contactId: contact.id, personId });
      await fetchPersons();
    } catch (err) {
      console.error("Failed to delete contact person:", err);
      alert(err.response?.data?.message || "Failed to delete contact person");
    }
  };

  const handleSetPrimary = async (personId) => {
    try {
      await setPrimaryContactPerson({ contactId: contact.id, personId });
      await fetchPersons();
    } catch (err) {
      console.error("Failed to set primary contact:", err);
      alert(err.response?.data?.message || "Failed to set primary contact");
    }
  };

  const companyName = contact?.company || contact?.company_name || "No Company Name";

  const getAvatarInitials = () => {
    if (contact?.logo) return contact.logo;
    if (companyName !== "No Company Name") return companyName.substring(0, 2).toUpperCase();
    return "?";
  };

  const getAvatarColors = () => {
    if (contact?.logoColor) return contact.logoColor;
    return { bg: "#e0f2fe", color: "#0284c7" };
  };

  const stageKey = contact?.pipelineStage || contact?.status;
  const stage = STAGES.find((s) => s.status === stageKey);

  const proposalStageIndex = STAGES.findIndex(s => s.id === "proposal");
  const currentStageIndex = STAGES.findIndex(s => s.status === stageKey);
  const showDealsTab = currentStageIndex >= proposalStageIndex;

  const emailRecipientOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    if (contact?.email) {
      options.push({
        value: contact.email,
        label: `${(`${contact.firstName || ""} ${contact.lastName || ""}`).trim() || companyName} — ${contact.email}`,
      });
      seen.add(contact.email);
    }

    (persons || []).forEach((p) => {
      if (p.email && !seen.has(p.email)) {
        options.push({
          value: p.email,
          label: `${getPersonFullName(p)}${p.primaryContact ? " (Primary)" : ""} — ${p.email}`,
        });
        seen.add(p.email);
      }
    });

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id, contact?.email, contact?.firstName, contact?.lastName, persons, companyName]);

  if (!open || !contact) return null;

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf") {
      setAttachmentError("Only PDF files are supported.");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachmentError("PDF must be smaller than 10MB.");
      return;
    }
    setAttachmentError("");
    setAttachment(file);
  };

  const resetLog = () => {
    setLogType("CALL");
    setLogSubject("");
    setNoteCategory(null);
    setNoteDetail("");
    setLogNotes("");
    setPrimaryPhone("");
    setSecondaryPhone("");
    setCallMode("notes");
    setPrimaryEmail([]);
    setCcEmails([]);
    setBccEmails([]);
    setAttachment(null);
    setAttachmentError("");
    setEmailBodyHtml("");
    setShowEmailBodyModal(false);
    setMeetingLink("");
    setMeetingDate("");
    setMeetingDuration("");
    setAudioBlob(null);
    setAudioDurationSeconds(0);
    setFormKey((k) => k + 1);
    setEditingLog(null);
  };

  const buildSubject = () => {
    if (logType === "NOTE") {
      if (!noteCategory) return "";
      const category = NOTE_CATEGORY_LABELS[noteCategory];
      return noteDetail.trim() ? `${category} - ${noteDetail.trim()}` : category;
    }
    return logSubject;
  };

  const isSubjectValid = logType === "NOTE" ? !!noteCategory : !!logSubject;

  const isEmailReady =
    logType !== "EMAIL" ||
    editingLog ||
    (primaryEmail.length > 0 && !!stripHtmlToText(emailBodyHtml));

  const showGenericNotesField =
    logType === "NOTE" ||
    logType === "MEETING" ||
    (logType === "CALL" && callMode === "notes");

  const handleEditNote = (log) => {
    if (log.type !== "NOTE") return;
    if (getQualificationBadge(log.subject)) return;
    setEditingLog(log);
    setLogType("NOTE");
    setLogSubject(log.subject || "");

    const subject = log.subject || "";
    let category = null;
    if (subject.startsWith(NOTE_CATEGORY_FOLLOWUP)) category = "FOLLOWUP";
    else if (subject.startsWith(NOTE_CATEGORY_OFFICE_VISIT)) category = "OFFICE_VISIT";
    else if (subject.startsWith(NOTE_CATEGORY_RESEARCH)) category = "RESEARCH";
    setNoteCategory(category);

    const categoryLabel = category ? NOTE_CATEGORY_LABELS[category] : "";
    const detail = categoryLabel && subject.startsWith(`${categoryLabel} - `)
      ? subject.slice(`${categoryLabel} - `.length)
      : "";
    setNoteDetail(detail);
    setLogNotes(log.notes || "");
    setTab("log");
  };

  const handleCancelEdit = () => {
    resetLog();
  };

  const handleSaveInteraction = async () => {
    if (!isSubjectValid) return;

    if (logType === "CALL" && callMode === "audio" && !audioBlob) {
      toast.error("Please record the call first, or switch to Notes.");
      return;
    }

    if (!editingLog && logType === "EMAIL") {
      if (primaryEmail.length === 0) {
        toast.error("Please select at least one recipient before sending the email.");
        return;
      }
      if (!stripHtmlToText(emailBodyHtml)) {
        toast.error("Please write the email body before sending it.");
        return;
      }
      const check = validateRecipientLists({
        to: primaryEmail,
        cc: ccEmails,
        bcc: canUseBcc ? bccEmails : [],
      });
      if (!check.ok) {
        toast.error(check.message);
        return;
      }
    }

    setSavingLog(true);
    try {
      if (editingLog) {
        const updatePayload = {
          type: "NOTE",
          subject: buildSubject(),
          contactId: contact.id,
          dealId: editingLog.dealId || null,
          notes: logNotes,
          durationMinutes: editingLog.durationMinutes ?? null,
          primaryPhone: editingLog.primaryPhone || null,
          secondaryPhone: editingLog.secondaryPhone || null,
          primaryEmail: editingLog.primaryEmail || null,
          secondaryEmail: editingLog.secondaryEmail || null,
          meetingLink: editingLog.meetingLink || null,
          occurredAt: editingLog.occurredAt,
        };
        await updateInteraction(editingLog.id, updatePayload);
        toast.success("Note updated.");
      } else if (logType === "EMAIL") {
        await sendEmailInteraction({
          contactId: contact.id,
          to: joinEmails(primaryEmail),
          cc: joinEmails(ccEmails),
          bcc: canUseBcc ? joinEmails(bccEmails) : undefined,
          subject: buildSubject(),
          body: emailBodyHtml,
          attachment,
        });
        toast.success(`Email sent to ${joinEmails(primaryEmail)}.`);
      } else {
        let interactionData = {
          type: logType,
          contactId: contact.id,
          contactName: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || companyName,
          subject: buildSubject(),
          notes: logNotes,
          occurredAt: new Date().toISOString(),
        };

        if (logType === "CALL") {
          interactionData = {
            ...interactionData,
            primaryPhone: primaryPhone || contact.phone,
            secondaryPhone,
          };
        } else if (logType === "MEETING") {
          interactionData = {
            ...interactionData,
            meetingLink,
            occurredAt: meetingDate ? new Date(meetingDate).toISOString() : new Date().toISOString(),
            durationMinutes: meetingDuration ? parseInt(meetingDuration) : null,
          };
        }

        if (logType === "CALL" && callMode === "audio") {
          await logInteractionWithRecording({
            type: "CALL",
            subject: interactionData.subject,
            contactId: contact.id,
            notes: "",
            primaryPhone: interactionData.primaryPhone,
            secondaryPhone,
            recording: audioBlob,
            recordingDurationSeconds: audioDurationSeconds,
            occurredAt: interactionData.occurredAt,
          });
        } else {
          await logInteraction(interactionData);
        }

        toast.success(
          logType === "CALL" ? "Call interaction logged." :
          logType === "MEETING" ? "Meeting logged." :
          "Interaction logged."
        );
      }

      resetLog();
      setTab("logs");
      if (onRefreshInteractions) await onRefreshInteractions();
    } catch (error) {
      console.error("Failed to save interaction:", error);
      toast.error(error.response?.data?.message || "Failed to save interaction. Please try again.");
    } finally {
      setSavingLog(false);
    }
  };

  const formatInteractionDisplay = (interaction) => ({
    ...interaction,
    date: interaction.occurredAt ? new Date(interaction.occurredAt).toLocaleDateString() : "No date",
    time: interaction.occurredAt ? new Date(interaction.occurredAt).toLocaleTimeString() : "",
  });

  // THIS IS THE UPDATED FILTER
  const interactionsWithNotes = (interactions || []).filter(
    (i) => (i.notes && i.notes.trim() !== "") || (i.type === "CALL" && i.hasRecording)
  );

  const hasMany = persons.length > 1;
  const firstPerson = persons[0];
  const firstPersonInfo = firstPerson ? getPersonDisplayInfo(firstPerson) : { name: "", initials: "?", contactInfo: "" };
  const firstPersonName = firstPerson ? getPersonFullName(firstPerson) : "";
  const personLimitReached = persons.length >= MAX_CONTACT_PERSONS;

  const TABS = [
    { id: "info", label: "Company Info" },
    { id: "people", label: "People" },
    { id: "interactions", label: "Interactions" },
    ...(showDealsTab ? [{ id: "deals", label: "Deals" }] : []),
    { id: "log", label: "Log Interaction" },
    { id: "logs", label: "Saved Logs" },
    { id: "notes", label: "History" },
  ];

  const avatarColors = getAvatarColors();

  if (showEmailBodyModal) {
    return (
      <EmailBodyEditorModal
        initialHtml={emailBodyHtml}
        onCancel={() => setShowEmailBodyModal(false)}
        onSave={(html) => {
          setEmailBodyHtml(html);
          setShowEmailBodyModal(false);
        }}
      />
    );
  }

  return (
    <Modal open={open} onClose={onClose} width="max-w-3xl">
      <div className="flex items-start justify-between flex-shrink-0 px-6 py-5 border-b border-neutral-100">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center text-[14px] font-bold flex-shrink-0"
            style={{ background: avatarColors.bg, color: avatarColors.color }}
          >
            {getAvatarInitials()}
          </div>
          <div>
            <h2 className="text-[16px] font-bold text-neutral-900">{companyName}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              {stage && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5"
                  style={{ background: `${stage.color}18`, color: stage.color }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: stage.color }} />
                  {stage.name}
                </span>
              )}
              <span className="text-[11px] text-neutral-400">{contact.industry}</span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center flex-shrink-0 rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100"
        >
          <X size={15} />
        </button>
      </div>

      <div className="flex flex-shrink-0 px-6 overflow-x-auto border-b border-neutral-100">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => handleTabChange(t.id)}
            className="px-4 py-3 text-[12px] font-semibold border-b-2 transition-all whitespace-nowrap"
            style={
              tab === t.id
                ? { color: "#009DB9", borderColor: "#009DB9" }
                : { color: "#a3a3a3", borderColor: "transparent" }
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">

        {tab === "info" && (
          <div className="flex flex-col gap-4 px-6 py-5">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
              <h3 className="text-[13px] font-semibold text-neutral-700 mb-3 flex items-center gap-2">
                <Users size={14} /> Company Contact Person
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Full Name", value: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || "—" },
                  { label: "Phone", value: contact.phone || "—" },
                  { label: "Job Title", value: contact.jobTitle || "—" },
                  { label: "Address", value: contact.address?.street || contact.address || "—" },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">{label}</div>
                    <div className="text-[13px] font-medium text-neutral-900 break-all">{value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Mail, label: "Email", value: contact.email || "—" },
                { icon: Briefcase, label: "Industry", value: contact.industry || "—" },
                { icon: Mail, label: "Website", value: contact.website || "—" },
                { icon: Users, label: "Social Media", value: contact.socialMedia || contact.linkedIn || "—" },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-2.5 p-3 bg-neutral-50 rounded-xl">
                  <Icon size={12} className="text-neutral-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">{label}</div>
                    <div className="text-[12px] text-neutral-800 font-medium break-all">{value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "people" && (
          <div className="flex flex-col gap-3 px-6 py-5">
            <div className="flex items-center justify-between">
              <h3 className="text-[13px] font-bold text-neutral-800">
                Contact Persons
                <span className="ml-1.5 text-[11px] font-semibold text-neutral-400">
                  ({persons.length}/{MAX_CONTACT_PERSONS})
                </span>
              </h3>
              <div className="flex items-center gap-2">
                {hasMany && (
                  <button onClick={() => setShowAllPersons(true)}
                    className="flex items-center gap-1 text-[11px] font-semibold text-[#009DB9] hover:underline">
                    View all <ChevronRight size={12} />
                  </button>
                )}
                <button
                  onClick={() => { setEditingPerson(null); setShowAddPerson(true); }}
                  disabled={personLimitReached}
                  title={personLimitReached ? `Maximum of ${MAX_CONTACT_PERSONS} contact persons reached` : undefined}
                  className="flex items-center gap-1.5 text-[11px] font-semibold px-3 h-7 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ color: "#009DB9", borderColor: "#009DB9" }}
                  onMouseEnter={(e) => { if (!personLimitReached) e.currentTarget.style.background = "rgba(0,157,185,0.06)"; }}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <UserPlus size={12} /> Add Person
                </button>
              </div>
            </div>

            {personLimitReached && (
              <p className="text-[11px] text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                Maximum of {MAX_CONTACT_PERSONS} contact persons reached for this company.
              </p>
            )}

            {loadingPersons ? (
              <div className="flex items-center justify-center py-8">
                <Loader size={22} className="animate-spin text-[#009DB9]" />
              </div>
            ) : persons.length === 0 ? (
              <div className="py-6 text-center border border-dashed border-neutral-200 rounded-xl">
                <UserPlus size={20} className="text-neutral-300 mx-auto mb-1.5" />
                <p className="text-[12px] text-neutral-400">No contact persons yet</p>
              </div>
            ) : (
              <div className="flex flex-col p-3.5 border border-neutral-100 rounded-xl bg-white sm:flex-row sm:items-start gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 self-start"
                  style={{ background: "#009DB9" }}>
                  {firstPersonInfo.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13px] font-semibold text-neutral-900">{firstPersonInfo.name}</span>
                      {firstPerson.primaryContact && (
                        <span className="text-[10px] font-semibold text-[#009DB9] bg-[#009DB9]/10 px-2 py-0.5 rounded-full">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {hasMany && (
                        <button onClick={() => setShowAllPersons(true)}
                          className="text-[11px] font-bold rounded-full px-2.5 py-1 transition-colors self-start sm:self-auto"
                          style={{ background: "rgba(0,157,185,0.1)", color: "#009DB9" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.18)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0,157,185,0.1)")}>
                          +{persons.length - 1} more
                        </button>
                      )}
                      <button
                        onClick={() => handleEditPerson(firstPerson)}
                        className="p-1.5 text-neutral-400 hover:text-[#009DB9] hover:bg-neutral-100 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete ${firstPersonName}?`)) {
                            handleDeletePerson(firstPerson.id);
                          }
                        }}
                        className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  <div className="text-[11px] text-neutral-400 mt-0.5 break-all">
                    {firstPersonInfo.contactInfo}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11px] text-neutral-500">
                    {firstPerson.jobTitle && <span>{firstPerson.jobTitle}</span>}
                    {firstPerson.department && <span>· {firstPerson.department}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "interactions" && (
          <div className="flex flex-col gap-4 px-6 py-5">
            <div className="grid grid-cols-2 gap-3">
              {LOG_TYPES.map(type => {
                const typeInteractions = (interactions || []).filter(i => i.type === type);
                const Icon = LOG_TYPE_COLORS[type]?.icon || FileText;
                const color = LOG_TYPE_COLORS[type]?.color || "#6b7280";
                return (
                  <div key={type} className="p-4 bg-neutral-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: `${color}15` }}>
                        <Icon size={14} style={{ color }} />
                      </div>
                      <span className="text-[13px] font-semibold text-neutral-800">{type}</span>
                    </div>
                    <p className="text-[24px] font-bold text-neutral-900">{typeInteractions.length}</p>
                    <p className="text-[11px] text-neutral-500 mt-1">Total {type.toLowerCase()}s</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-2 p-4 bg-[#009DB9]/5 rounded-xl border border-[#009DB9]/20">
              <h3 className="text-[13px] font-semibold text-neutral-800 mb-2">Recent Activity</h3>
              {interactions && interactions.length > 0 ? (
                <div className="space-y-2">
                  {interactions.slice(0, 3).map(interaction => {
                    const formattedDate = interaction.occurredAt
                      ? new Date(interaction.occurredAt).toLocaleDateString()
                      : "Unknown";
                    const qualBadge = getQualificationBadge(interaction.subject);
                    return (
                      <div key={interaction.id} className="flex items-center gap-2 text-[12px] text-neutral-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#009DB9]" />
                        <span className="font-medium">{interaction.type}:</span>
                        <span className="flex-1 truncate">
                          {qualBadge ? NOTE_CATEGORY_QUALIFICATION : interaction.subject}
                        </span>
                        <span className="text-[10px] text-neutral-400">{formattedDate}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[12px] text-neutral-500">No interactions yet. Click "Log Interaction" to get started.</p>
              )}
            </div>
          </div>
        )}

        {tab === "deals" && showDealsTab && (
          <div className="flex flex-col gap-3 px-6 py-5">
            <div className="flex items-center justify-between">
              <p className="text-[12px] text-neutral-400">
                All deals linked to <strong className="text-neutral-700">{companyName}</strong>
              </p>
              <button
                onClick={() => onOpenDealForm({ contact, deal: null, onSuccess: fetchDeals })}
                className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-xl text-white transition-colors"
                style={{ background: "#009DB9" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#007a90")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#009DB9")}
              >
                <Plus size={13} /> Add Deal
              </button>
            </div>

            {loadingDeals ? (
              <div className="flex items-center justify-center py-10">
                <Loader size={24} className="animate-spin text-[#009DB9]" />
              </div>
            ) : deals.length > 0 ? (
              <div className="flex flex-col gap-2">
                {deals.map((deal) => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    onEdit={(d) => onOpenDealForm({ contact, deal: d, onSuccess: fetchDeals })}
                  />
                ))}
              </div>
            ) : (
              <div className="py-12 text-center bg-neutral-50 rounded-xl">
                <Briefcase size={32} className="mx-auto mb-2 text-neutral-300" />
                <p className="text-[13px] text-neutral-500">No deals yet</p>
                <p className="text-[11px] text-neutral-400 mt-1">Click "Add Deal" to create the first deal for this company</p>
              </div>
            )}
          </div>
        )}

        {tab === "log" && (
          <div className="flex flex-col gap-4 px-6 py-5">
            {editingLog && (
              <div className="flex items-center justify-between px-3 py-2 text-[11px] font-semibold border rounded-lg text-amber-700 bg-amber-50 border-amber-200">
                <span>Editing existing note</span>
                <button onClick={handleCancelEdit} className="underline hover:no-underline">
                  Cancel edit
                </button>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Interaction Type</label>
              <div className="grid grid-cols-4 gap-2">
                {LOG_TYPES.map((t) => {
                  const s = LOG_TYPE_COLORS[t];
                  return (
                    <button
                      key={t}
                      onClick={() => { if (!editingLog) setLogType(t); }}
                      disabled={!!editingLog}
                      className="py-2 rounded-lg border text-[12px] font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={
                        logType === t
                          ? { background: s.bg, color: s.color, borderColor: s.color + "60" }
                          : { background: "#fff", color: "#525252", borderColor: "#e5e5e5" }
                      }
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            {logType === "NOTE" ? (
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Note Category *</label>
                <div className="px-1 py-1">
                  <NoteCategoryCheckboxes value={noteCategory} onChange={setNoteCategory} />
                </div>
                {!noteCategory && (
                  <p className="text-[11px] text-amber-600">Select Research Mapping, Follow-up, or Office Visit to continue.</p>
                )}
                <input
                  type="text"
                  value={noteDetail}
                  onChange={(e) => setNoteDetail(e.target.value)}
                  placeholder="Title"
                  className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors"
                />
                {noteCategory && (
                  <p className="text-[10px] text-neutral-400">
                    Subject will be saved as: <strong>{buildSubject()}</strong>
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Subject *</label>
                <input
                  type="text"
                  value={logSubject}
                  onChange={(e) => setLogSubject(e.target.value)}
                  placeholder="e.g. Proposal follow-up"
                  className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors"
                />
              </div>
            )}

            {logType === "CALL" && !editingLog && (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Primary Phone *</label>
                  <input type="tel" value={primaryPhone} onChange={(e) => setPrimaryPhone(e.target.value)}
                    placeholder={contact.phone || "Enter primary phone number"}
                    className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors" />
                  {contact.phone && <p className="text-[10px] text-neutral-400 mt-0.5">Company phone: {contact.phone}</p>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Secondary Phone</label>
                  <input type="tel" value={secondaryPhone} onChange={(e) => setSecondaryPhone(e.target.value)}
                    placeholder="Secondary phone number (optional)"
                    className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors" />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Record As</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setCallMode("notes")}
                      className="py-2 rounded-lg border text-[12px] font-medium transition-all"
                      style={
                        callMode === "notes"
                          ? { background: "#fef3c7", color: "#d97706", borderColor: "#d9770660" }
                          : { background: "#fff", color: "#525252", borderColor: "#e5e5e5" }
                      }
                    >
                      Notes
                    </button>
                    <button
                      type="button"
                      onClick={() => setCallMode("audio")}
                      className="py-2 rounded-lg border text-[12px] font-medium transition-all"
                      style={
                        callMode === "audio"
                          ? { background: "#dcfce7", color: "#16a34a", borderColor: "#16a34a60" }
                          : { background: "#fff", color: "#525252", borderColor: "#e5e5e5" }
                      }
                    >
                      Audio Recording
                    </button>
                  </div>
                </div>

                {callMode === "audio" && (
                  <AudioRecorder
                    key={formKey}
                    disabled={savingLog}
                    onRecordingComplete={(blob, url, duration) => {
                      setAudioBlob(blob);
                      setAudioDurationSeconds(duration);
                    }}
                    onClear={() => {
                      setAudioBlob(null);
                      setAudioDurationSeconds(0);
                    }}
                    onError={(msg) => toast.error(msg)}
                  />
                )}
              </>
            )}

            {logType === "EMAIL" && !editingLog && (
              <>
                <EmailRecipientsField
                  options={emailRecipientOptions}
                  to={primaryEmail}
                  cc={ccEmails}
                  bcc={bccEmails}
                  onToChange={setPrimaryEmail}
                  onCcChange={setCcEmails}
                  onBccChange={canUseBcc ? setBccEmails : undefined}
                  showBcc={canUseBcc}
                  disabled={savingLog}
                />
                {emailRecipientOptions.length === 0 && (
                  <p className="text-[10px] text-amber-600 -mt-2">
                    No registered email found for this company yet. Add a contact person or a company email first.
                  </p>
                )}

                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Attachment (PDF only)</label>
                  {!attachment ? (
                    <label className="flex items-center justify-center gap-2 h-9 border border-dashed border-neutral-300 rounded-lg text-[12px] text-neutral-500 cursor-pointer hover:border-[#009DB9] hover:text-[#009DB9] transition-colors">
                      <Paperclip size={13} /> Attach PDF
                      <input type="file" accept="application/pdf" className="hidden" onChange={handleAttachmentChange} />
                    </label>
                  ) : (
                    <div className="flex items-center justify-between px-3 border rounded-lg h-9 border-neutral-200 bg-neutral-50">
                      <span className="text-[12px] text-neutral-700 truncate">{attachment.name}</span>
                      <button type="button" onClick={() => setAttachment(null)} className="flex-shrink-0 text-neutral-400 hover:text-red-500">
                        <X size={13} />
                      </button>
                    </div>
                  )}
                  {attachmentError && <p className="text-[10px] text-red-500 mt-0.5">{attachmentError}</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Email Body *</label>
                    {emailBodyHtml && (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setShowEmailBodyModal(true)}
                          className="flex items-center gap-1 text-[11px] font-semibold text-[#009DB9] hover:underline"
                        >
                          <Edit2 size={11} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setEmailBodyHtml("")}
                          className="flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:underline"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    )}
                  </div>

                  {!emailBodyHtml ? (
                    <button
                      type="button"
                      onClick={() => setShowEmailBodyModal(true)}
                      className="flex items-center justify-center gap-2 h-9 border border-dashed border-neutral-300 rounded-lg text-[12px] text-neutral-500 hover:border-[#009DB9] hover:text-[#009DB9] transition-colors"
                    >
                      <Plus size={13} /> Write Email Body
                    </button>
                  ) : (
                    <div
                      className="border border-neutral-200 rounded-lg px-3 py-2 text-[13px] text-neutral-800 bg-neutral-50 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                      dangerouslySetInnerHTML={{ __html: emailBodyHtml }}
                    />
                  )}
                </div>
              </>
            )}

            {logType === "MEETING" && !editingLog && (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Meeting Link</label>
                  <div className="relative">
                    <Link2 className="absolute left-3 top-2.5 text-neutral-400" size={14} />
                    <input type="url" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)}
                      placeholder="https://meet.google.com/..."
                      className="w-full h-9 pl-9 border border-neutral-200 rounded-lg text-[13px] outline-none focus:border-[#009DB9] transition-colors" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Meeting Date</label>
                    <input type="datetime-local" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)}
                      className="h-9 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9] transition-colors" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">Duration (mins)</label>
                    <input type="number" value={meetingDuration} onChange={(e) => setMeetingDuration(e.target.value)}
                      placeholder="30"
                      className="h-9 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9] transition-colors" />
                  </div>
                </div>
              </>
            )}

            {showGenericNotesField && (
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
                  {logType === "NOTE" ? "Note Body *" : "Notes"}
                </label>
                <textarea
                  ref={notesTextareaRef}
                  rows={3}
                  value={logNotes}
                  onChange={(e) => setLogNotes(e.target.value)}
                  onInput={resizeNotesTextarea}
                  placeholder={logType === "NOTE" ? "Enter your notes here..." : "Additional notes..."}
                  className="border border-neutral-200 rounded-lg px-3 py-2 text-[12px] outline-none focus:border-[#009DB9] resize-none transition-colors overflow-hidden"
                  style={{ minHeight: "72px" }}
                />
              </div>
            )}

            <button
              onClick={handleSaveInteraction}
              disabled={!isSubjectValid || !isEmailReady || savingLog}
              className="w-full py-2.5 rounded-xl text-white text-[13px] font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ background: "#009DB9" }}
              onMouseEnter={(e) => { if (isSubjectValid && isEmailReady && !savingLog) e.currentTarget.style.background = "#007a90"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}
            >
              {savingLog ? (
                <Loader size={16} className="animate-spin" />
              ) : logType === "EMAIL" && !editingLog ? (
                <Send size={14} />
              ) : null}
              {savingLog
                ? (logType === "EMAIL" && !editingLog ? "Sending..." : "Saving...")
                : editingLog
                ? "Update Note"
                : logType === "EMAIL"
                ? "Send Email"
                : "Save Interaction"}
            </button>
            <p className="text-[11px] text-neutral-400 text-center -mt-2">
              {editingLog ? "Updates the existing note." : "Form clears after saving so you can log another interaction."}
            </p>
          </div>
        )}

        {tab === "logs" && (
          <div className="flex flex-col gap-3 px-6 py-5">
            <div className="flex items-center justify-between">
              <p className="text-[12px] text-neutral-400">
                All interactions logged for <strong className="text-neutral-700">{companyName}</strong>
              </p>
              <button
                onClick={onRefreshInteractions}
                className="text-[11px] text-[#009DB9] hover:underline"
                disabled={loadingInteractions}
              >
                {loadingInteractions ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            {loadingInteractions ? (
              <div className="flex items-center justify-center py-8">
                <Loader size={24} className="animate-spin text-[#009DB9]" />
              </div>
            ) : interactions && interactions.length > 0 ? (
              interactions.map((log) => {
                const formattedLog = formatInteractionDisplay(log);
                const s = LOG_TYPE_COLORS[log.type];
                const Icon = s?.icon || FileText;
                const qualBadge = getQualificationBadge(log.subject);
                const edited = isEdited(log);
                return (
                  <div key={log.id} className="flex gap-3 p-4 transition-colors bg-white border border-neutral-100 rounded-xl hover:bg-neutral-50/60">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: s?.bg || "#f3f4f6" }}>
                      <Icon size={13} style={{ color: s?.color || "#6b7280" }} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[13px] font-semibold text-neutral-800">
                            {qualBadge ? NOTE_CATEGORY_QUALIFICATION : log.subject}
                          </span>
                          {qualBadge && (
                            <span
                              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                qualBadge.isQualified
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-orange-100 text-orange-700"
                              }`}
                            >
                              {qualBadge.label}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center flex-shrink-0 gap-2">
                          <span className="text-[11px] text-neutral-400">{formattedLog.date} · {formattedLog.time}</span>
                          {edited && (
                            <span className="text-[10px] font-semibold text-neutral-400 italic">· edited</span>
                          )}
                          {log.type === "NOTE" && !qualBadge && (
                            <button
                              onClick={() => handleEditNote(log)}
                              className="p-1 text-neutral-400 hover:text-[#009DB9] hover:bg-neutral-100 rounded transition-colors"
                              title="Edit note"
                            >
                              <Edit2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>

                      {(log.userName || log.userId) && (
                        <div className="flex items-center gap-1 mt-1 text-[10px] text-neutral-400">
                          <UserIcon size={9} /> Logged by {getLoggedByDisplay(log)}
                        </div>
                      )}

                      {log.type === "CALL" && (log.primaryPhone || log.secondaryPhone) && (
                        <div className="mt-1 space-y-0.5">
                          {log.primaryPhone && <div className="text-[11px] text-neutral-500"><span className="font-medium">Primary:</span> {log.primaryPhone}</div>}
                          {log.secondaryPhone && <div className="text-[11px] text-neutral-500"><span className="font-medium">Secondary:</span> {log.secondaryPhone}</div>}
                        </div>
                      )}

                      {log.type === "CALL" && <RecordingPlayer interaction={log} />}

                      {log.type === "EMAIL" && (log.primaryEmail || log.secondaryEmail || log.bccEmail) && (
                        <div className="mt-1 space-y-0.5">
                          {log.primaryEmail && (
                            <div className="text-[11px] text-neutral-500">
                              <span className="font-medium">To:</span> {log.primaryEmail}
                            </div>
                          )}
                          {log.secondaryEmail && (
                            <div className="text-[11px] text-neutral-500">
                              <span className="font-medium">CC:</span> {log.secondaryEmail}
                            </div>
                          )}
                          {log.bccEmail && (
                            <div className="text-[11px] text-neutral-500">
                              <span className="font-medium">BCC:</span> {log.bccEmail}
                            </div>
                          )}
                        </div>
                      )}

                      {log.type === "EMAIL" && <AttachmentLink interaction={log} />}

                      {log.type === "MEETING" && log.meetingLink && (
                        <div className="mt-1">
                          <a href={log.meetingLink} target="_blank" rel="noopener noreferrer"
                            className="text-[11px] text-[#009DB9] hover:underline flex items-center gap-1">
                            <Video size={10} /> Meeting Link
                          </a>
                        </div>
                      )}

                      <InteractionNotes notes={log.notes || "No notes"} className="text-[12px] text-neutral-500 mt-1" />

                      {log.durationMinutes && (
                        <div className="flex items-center gap-1 mt-1.5 text-[11px] text-neutral-400">
                          <Clock size={10} strokeWidth={2} /> {log.durationMinutes} minutes
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-[13px] text-neutral-400">No interactions logged yet.</div>
            )}
          </div>
        )}

        {tab === "notes" && (
          <div className="flex flex-col gap-4 px-6 py-5">
            <div className="flex items-center justify-between">
              <p className="text-[12px] text-neutral-400">
                Discussion history from interactions with <strong className="text-neutral-700">{companyName}</strong>
              </p>
              <button
                onClick={onRefreshInteractions}
                className="text-[11px] text-[#009DB9] hover:underline"
                disabled={loadingInteractions}
              >
                {loadingInteractions ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            {loadingInteractions ? (
              <div className="flex items-center justify-center py-8">
                <Loader size={24} className="animate-spin text-[#009DB9]" />
              </div>
            ) : interactionsWithNotes.length > 0 ? (
              <div className="flex flex-col gap-3">
                {interactionsWithNotes.map((interaction) => {
                  const formattedDate = interaction.occurredAt
                    ? new Date(interaction.occurredAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                    : "Unknown date";
                  const formattedTime = interaction.occurredAt
                    ? new Date(interaction.occurredAt).toLocaleTimeString()
                    : "";
                  const s = LOG_TYPE_COLORS[interaction.type];
                  const Icon = s?.icon || FileText;
                  const qualBadge = getQualificationBadge(interaction.subject);
                  const edited = isEdited(interaction);

                  return (
                    <div key={interaction.id} className="p-4 transition-shadow bg-white border border-neutral-100 rounded-xl hover:shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg"
                          style={{ background: s?.bg || "#f3f4f6" }}>
                          <Icon size={13} style={{ color: s?.color || "#6b7280" }} strokeWidth={2} />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[12px] font-semibold text-neutral-800">
                                {qualBadge ? NOTE_CATEGORY_QUALIFICATION : interaction.subject}
                              </span>
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                                  qualBadge
                                    ? qualBadge.isQualified
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-orange-100 text-orange-700"
                                    : "bg-neutral-100 text-neutral-600"
                                }`}
                              >
                                {qualBadge ? qualBadge.label : interaction.type}
                              </span>
                            </div>
                            <div className="flex items-center flex-shrink-0 gap-2">
                              <span className="text-[11px] text-neutral-400">{formattedDate} · {formattedTime}</span>
                              {edited && (
                                <span className="text-[10px] font-semibold text-neutral-400 italic">· edited</span>
                              )}
                              {interaction.type === "NOTE" && !qualBadge && (
                                <button
                                  onClick={() => handleEditNote(interaction)}
                                  className="p-1 text-neutral-400 hover:text-[#009DB9] hover:bg-neutral-100 rounded transition-colors"
                                  title="Edit note"
                                >
                                  <Edit2 size={12} />
                                </button>
                              )}
                            </div>
                          </div>
                          {(interaction.userName || interaction.userId) && (
                            <div className="flex items-center gap-1 mb-2 text-[10px] text-neutral-400">
                              <UserIcon size={9} /> Logged by {getLoggedByDisplay(interaction)}
                            </div>
                          )}
                          
                          <InteractionNotes notes={interaction.notes} className="text-[13px] text-neutral-700 leading-relaxed" />
                          
                          {/* Added Recording Player and Attachment link here for the History tab */}
                          {interaction.type === "CALL" && <RecordingPlayer interaction={interaction} />}
                          {interaction.type === "EMAIL" && <AttachmentLink interaction={interaction} />}

                          {interaction.durationMinutes && (
                            <div className="flex items-center gap-1 mt-2 text-[11px] text-neutral-400">
                              <Clock size={10} strokeWidth={2} /> Duration: {interaction.durationMinutes} minutes
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 text-center bg-neutral-50 rounded-xl">
                <FileText size={32} className="mx-auto mb-2 text-neutral-300" />
                <p className="text-[13px] text-neutral-500">No discussion history available</p>
                <p className="text-[11px] text-neutral-400 mt-1">Add notes when logging interactions</p>
              </div>
            )}
          </div>
        )}
      </div>

      <AddContactPersonModal
        open={showAddPerson}
        onClose={() => {
          setShowAddPerson(false);
          setEditingPerson(null);
        }}
        onAdd={handleSavePerson}
        initialData={editingPerson}
      />
      <AllContactPersonsModal
        open={showAllPersons}
        onClose={() => setShowAllPersons(false)}
        company={contact}
        persons={persons}
        onAddPerson={() => {
          if (personLimitReached) return;
          setShowAllPersons(false);
          setEditingPerson(null);
          setShowAddPerson(true);
        }}
        onEditPerson={(person) => {
          setShowAllPersons(false);
          handleEditPerson(person);
        }}
        onDeletePerson={handleDeletePerson}
        onSetPrimary={handleSetPrimary}
      />
    </Modal>
  );
}